/** Which action failed. The copy differs; the codes do not. */
export type AuthAction = 'sign-in' | 'sign-out'

/**
 * Turn a Firebase Auth error into a screen message.
 *
 * Closing the popup is not a failure. Provider-not-enabled and
 * unauthorized-domain name the human follow-ups (Google on the internal
 * tenant, origin allow-list) rather than a code defect. Unrecognized
 * codes get a generic line — never the raw SDK string.
 *
 * The action matters: a failed **sign-out** told to "check the connection and
 * try again" alongside sign-in wording reads as though nothing is wrong, when
 * in fact the user is still signed in. On a privileged console that is the
 * message that has to be right.
 *
 * @param error - The value the auth call rejected with.
 * @param action - Which action was attempted.
 * @returns A message to show, or `null` to show nothing.
 */
export function formatAuthError(
  error: unknown,
  action: AuthAction = 'sign-in'
): string | null {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String(error.code)
      : ''

  if (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request'
  ) {
    return null
  }
  if (code === 'auth/popup-blocked') {
    return 'The sign-in popup was blocked. Allow popups for this site and try again.'
  }
  if (code === 'auth/operation-not-allowed') {
    return 'Google sign-in is not enabled on this tenant yet.'
  }
  if (code === 'auth/unauthorized-domain') {
    return 'This origin is not allow-listed for Google sign-in.'
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'This Google account is not linked on the tenant yet.'
  }
  // The tenant creates no account on first sign-in, so this is what an
  // employee who has never been provisioned actually gets. It arrives AFTER a
  // successful Google handshake, which makes the default wording ("something
  // went wrong") read as a bug in the console rather than a missing account.
  if (code === 'auth/admin-restricted-operation') {
    return 'No internal account exists for this Google account yet. Ask IT to provision one.'
  }
  if (code === 'auth/network-request-failed') {
    return action === 'sign-out'
      ? 'Network error signing out — you are still signed in. Try again, or close this tab.'
      : 'Network error during sign-in. Check the connection and try again.'
  }
  if (code === 'auth/too-many-requests') {
    return action === 'sign-out'
      ? 'Too many attempts — you are still signed in. Wait and try again, or close this tab.'
      : 'Too many sign-in attempts. Wait and try again.'
  }
  if (code === 'auth/user-disabled') {
    return action === 'sign-out'
      ? 'This account is disabled — you are still signed in. Close this tab.'
      : 'This account is disabled.'
  }

  return action === 'sign-out'
    ? 'Sign-out failed — you are still signed in. Try again, or close this tab.'
    : 'Google sign-in failed.'
}
